import React, { useState, useEffect, useRef, useCallback } from 'react';
import { app, auth } from './firebase';
import { signInWithEmailAndPassword, sendPasswordResetEmail, signOut, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, getDoc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const db = getFirestore(app);
const storage = getStorage(app);

export default function ConsultantPortalView({ theme, toggleTheme }) {
  const [portalUser, setPortalUser] = useState(null);
  const [consultantDocId, setConsultantDocId] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authLoading, setAuthLoading] = useState(true);
  
  // --- Timesheet Entry State ---
  const [month, setMonth] = useState(new Date().toLocaleString('default', { month: 'long' }));
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [days, setDays] = useState({}); 
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState('Draft');
  const [uploading, setUploading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  // --- History State ---
  const [submissionHistory, setSubmissionHistory] = useState([]);

  // --- IDLE TIMER REFS ---
  const idleTimerRef = useRef(null);
  const events = ['mousemove', 'keydown', 'click', 'scroll'];

  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const years = [2024, 2025, 2026, 2027];

  // --- 1. AUTH LISTENER (STRICT SECURITY) ---
  useEffect(() => {
      // Safety Timeout to prevent infinite loading
      const safetyTimeout = setTimeout(() => {
          if (authLoading) {
              console.warn("Portal auth check timed out. Defaulting to logged out.");
              setAuthLoading(false);
          }
      }, 2000);

      const unsub = onAuthStateChanged(auth, async (currentUser) => {
          clearTimeout(safetyTimeout);

          if (currentUser) {
              try {
                  // A. Force Token Refresh to get latest claims
                  const tokenResult = await currentUser.getIdTokenResult(true);
                  const claims = tokenResult.claims;

                  // SECURITY CHECK 1: If Employee, Redirect to Admin Dashboard
                  if (claims.role === 'internal') {
                      window.location.href = '/';
                      return;
                  }
                  
                  // B. ROLE CHECK: Must strictly be a 'consultant'
                  if (claims.role !== 'consultant') {
                      console.warn("Unauthorized access attempt (Non-Consultant).");
                      await signOut(auth);
                      alert("Access Denied: This area is for Consultants only.");
                      setPortalUser(null);
                      setAuthLoading(false);
                      return;
                  }

                  // C. Success
                  setPortalUser(currentUser);
              } catch (e) {
                  console.error("Security check failed:", e);
                  await signOut(auth);
                  setPortalUser(null);
              }
          } else {
              setPortalUser(null);
          }
          setAuthLoading(false);
      });

      return () => {
          unsub();
          clearTimeout(safetyTimeout);
      };
  }, []);

  const [error, setError] = useState('');

  // --- LOGIN HANDLER ---
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    try {
      // The onAuthStateChanged listener will handle the redirect/role check logic
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) { 
      console.error(err);
      setError("Invalid email or password. Please try again.");
    }
  };

  const handleReset = async () => {
      if(!email) {
          setError("Please enter your email address above first.");
          return;
      }
      try {
        await sendPasswordResetEmail(auth, email);
        alert("Password reset link sent to your email."); // Keep alert for success or use toast if available
      } catch (err) { setError(err.message); }
  };

  // --- LOGOUT LOGIC ---
  const handleLogout = useCallback(() => {
      signOut(auth).then(() => {
          setPortalUser(null);
          setConsultantDocId(null);
      });
  }, []);

  // --- IDLE TIMER (30 MINS) ---
  const resetIdleTimer = useCallback(() => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      
      if (portalUser) {
          // 30 Minutes = 1,800,000 ms
          idleTimerRef.current = setTimeout(() => {
              console.log("Portal session timed out due to inactivity.");
              handleLogout();
          }, 1800000);
      }
  }, [portalUser, handleLogout]);

  useEffect(() => {
      if (!portalUser) return;

      const handleOffline = () => {
          console.log("Connection lost. Logging out.");
          handleLogout();
      };
      window.addEventListener('offline', handleOffline);

      resetIdleTimer();
      events.forEach(event => window.addEventListener(event, resetIdleTimer));

      return () => {
          window.removeEventListener('offline', handleOffline);
          events.forEach(event => window.removeEventListener(event, resetIdleTimer));
          if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      };
  }, [portalUser, resetIdleTimer, handleLogout]);


  // --- IDENTIFY CONSULTANT ID ---
  // Retrieves the internal Database ID from custom claims or falls back to UID
  useEffect(() => {
      if (portalUser) {
          portalUser.getIdTokenResult().then((idTokenResult) => {
              const linkedId = idTokenResult.claims.consultantId || portalUser.uid;
              setConsultantDocId(linkedId);
          });
      }
  }, [portalUser]);

  // --- LOAD TIMESHEET (Single Entry) ---
  useEffect(() => {
      if(!portalUser || !consultantDocId) return;
      setLoadingData(true);
      
      const loadSheet = async () => {
          try {
              const docId = `${year}_${month}_${consultantDocId}`;
              const docRef = doc(db, 'timesheets', docId);
              const snap = await getDoc(docRef);
              
              if(snap.exists()) {
                  const data = snap.data();
                  setDays(data.days || {});
                  setStatus(data.status || 'Draft');
              } else {
                  setDays({});
                  setStatus('Draft');
              }
          } catch (err) {
              console.error("Error loading timesheet:", err);
          } finally {
              setLoadingData(false);
          }
      };
      loadSheet();
  }, [portalUser, consultantDocId, month, year]);

  // --- LOAD SUBMISSION HISTORY ---
  useEffect(() => {
      if(!portalUser || !consultantDocId) return;
      
      const fetchHistory = async () => {
          try {
              // Query for BOTH Auth UID and Doc ID to show all relevant history
              const idsToQuery = [portalUser.uid];
              if (consultantDocId !== portalUser.uid) {
                  idsToQuery.push(consultantDocId);
              }

              const q = query(
                  collection(db, "timesheets"), 
                  where("consultantId", "in", idsToQuery)
              );
              const snap = await getDocs(q);
              const data = snap.docs.map(d => d.data());
              
              data.sort((a,b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
              setSubmissionHistory(data);
          } catch (err) {
              console.error("Error loading history", err);
          }
      };
      fetchHistory();
  }, [portalUser, consultantDocId, status]); 

  // --- TIMESHEET HANDLERS ---
  const handleDayChange = (day, hours) => {
      if(status !== 'Draft' && status !== 'Rejected') return; 
      setDays(prev => ({...prev, [day]: hours}));
  };

  const quickFill = () => {
      const daysInMonth = new Date(year, months.indexOf(month) + 1, 0).getDate();
      const newDays = {};
      for(let d = 1; d <= daysInMonth; d++) {
          const date = new Date(year, months.indexOf(month), d);
          const dayOfWeek = date.getDay(); 
          if(dayOfWeek !== 0 && dayOfWeek !== 6) newDays[d] = 8; // Mon-Fri only
      }
      setDays(newDays);
  };

  const handleSubmit = async () => {
      if(!file && status === 'Draft' && !window.confirm("Submit without uploading a file?")) return;
      setUploading(true);

      try {
          let fileUrl = '';
          if(file) {
              const storageRef = ref(storage, `portal_proofs/${year}/${month}/${consultantDocId}_${file.name}`);
              await uploadBytes(storageRef, file);
              fileUrl = await getDownloadURL(storageRef);
          }

          const totalHours = Object.values(days).reduce((a, b) => a + Number(b), 0);
          
          // --- GET CLIENT NAME & CUSTOMER ID FROM ACTIVE CONTRACTS ---
          let clientName = '';
          let customerId = '';
          try {
              const contractsQ = query(
                  collection(db, "active_contracts"), 
                  where("consultantId", "==", consultantDocId),
                  where("status", "==", "Active")
              );
              const contractsSnap = await getDocs(contractsQ);
              if (!contractsSnap.empty) {
                  const contractData = contractsSnap.docs[0].data();
                  clientName = contractData.client;
                  customerId = contractData.customerId || '';
              }
          } catch (e) {
              console.warn("Could not fetch client name for timesheet:", e);
          }

          const docId = `${year}_${month}_${consultantDocId}`;
          
          await setDoc(doc(db, "timesheets", docId), {
              consultantId: consultantDocId, 
              consultantName: portalUser.displayName || 'Consultant',
              client: clientName,
              customerId: customerId,
              month, year,
              days,
              totalHours,
              proofUrl: fileUrl || undefined,
              status: 'Submitted',
              updatedAt: new Date().toISOString(),
              source: 'Portal'
          }, { merge: true });

          setStatus('Submitted');
          alert("Timesheet Submitted Successfully!");
      } catch (err) {
          console.error(err);
          alert("Error submitting timesheet: " + err.message);
      } finally {
          setUploading(false);
      }
  };

  const getStatusBadge = (status) => {
      switch(status) {
          case 'Approved': return 'bg-success';
          case 'Submitted': return 'bg-warning text-dark';
          case 'Rejected': return 'bg-danger';
          default: return 'bg-secondary';
      }
  };

  // --- RENDER: LOADING STATE ---
  if (authLoading) {
      return (
          <div className="d-flex align-items-center justify-content-center vh-100 bg-body">
              <div className="text-main">Loading Portal...</div>
          </div>
      );
  }

  // --- RENDER: LOGIN SCREEN ---
  if (!portalUser) {
    return (
      <div className="d-flex align-items-center justify-content-center vh-100 bg-transparent-theme">
        <div className="card bg-card border-theme shadow-lg p-4" style={{maxWidth: '400px', width: '100%'}}>
          <div className="text-center mb-4">
            <h2 className="fw-bold text-main">Nettic<span className="text-accent">.</span></h2>
            <p className="text-muted-theme small">Consultant Portal Login</p>
          </div>
          
          {error && (
            <div className="alert alert-danger py-2 small d-flex align-items-center">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                {error}
            </div>
          )}

          <form onSubmit={handleLogin}>
            <div className="mb-3">
                <label className="form-label small text-muted-theme">Email</label>
                <input className="form-control" type="email" value={email} onChange={e=>setEmail(e.target.value)} required />
            </div>
            <div className="mb-4">
                <label className="form-label small text-muted-theme">Password</label>
                <input type="password" className="form-control" value={password} onChange={e=>setPassword(e.target.value)} required />
            </div>
            <button className="btn btn-primary w-100 mb-3">Login</button>
            <button type="button" className="btn btn-link w-100 text-muted-theme small" onClick={handleReset}>Forgot Password?</button>
          </form>
          <div className="mt-2 text-center">
            <small className="text-muted-theme">Consultant access only. Contact your account manager for help.</small>
          </div>
        </div>
      </div>
    );
  }

  // --- RENDER: MAIN PORTAL UI ---
  const daysInMonth = new Date(year, months.indexOf(month) + 1, 0).getDate();
  const daysArray = Array.from({length: daysInMonth}, (_, i) => i + 1);

  return (
    <div className="min-vh-100 bg-body text-main">
      {/* NAVBAR */}
      <nav className="navbar border-bottom border-theme px-4 shadow-sm bg-card">
        <span className="navbar-brand mb-0 h1 fw-bold text-main">Nettic<span className="text-accent">.</span> <span className="text-muted-theme ms-1 small">Consultant Portal</span></span>
        <div className="d-flex align-items-center gap-3">
            <span className="text-main small d-none d-md-inline">Welcome, {portalUser.displayName || portalUser.email}</span>
            <button className="btn btn-sm btn-outline-secondary border-theme" onClick={toggleTheme} title="Toggle Theme">
                {theme === 'dark' ? <i className="bi bi-moon-stars-fill"></i> : <i className="bi bi-sun-fill"></i>}
            </button>
            <button className="btn btn-outline-danger btn-sm" onClick={handleLogout}>Logout</button>
        </div>
      </nav>

      <div className="container py-5">
        <div className="row">
            
            {/* --- LEFT COLUMN: TIMESHEET ENTRY --- */}
            <div className="col-lg-8 mb-4">
                <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
                    <h3 className="fw-bold text-main">Timesheet Entry</h3>
                    <div className="d-flex gap-2">
                        <select className="form-select" value={month} onChange={e=>setMonth(e.target.value)}>
                            {months.map(m=><option key={m}>{m}</option>)}
                        </select>
                        <select className="form-select" value={year} onChange={e=>setYear(e.target.value)}>
                            {years.map(y=><option key={y}>{y}</option>)}
                        </select>
                    </div>
                </div>

                {loadingData ? <div className="text-center py-5 text-muted-theme">Loading data...</div> : (
                    <>
                        <div className="card shadow-sm border-theme bg-card mb-4">
                            <div className="card-body">
                                <div className="d-flex justify-content-between mb-4 pb-3 border-bottom border-theme">
                                    <div>
                                        <h5 className="mb-1 text-main">Status: <span className={`badge ${getStatusBadge(status)}`}>{status}</span></h5>
                                        <small className="text-muted-theme">Total Hours: {Object.values(days).reduce((a,b)=>a+Number(b),0)}</small>
                                    </div>
                                    {(status === 'Draft' || status === 'Rejected') && (
                                        <button className="btn btn-sm btn-outline-primary" onClick={quickFill}>Quick Fill (Mon-Fri)</button>
                                    )}
                                </div>

                                <div className="row g-2">
                                    {daysArray.map(d => (
                                        <div key={d} className="col-4 col-md-2 col-lg-2">
                                            <div className="form-floating">
                                                <input 
                                                    type="number" 
                                                    className="form-control text-center px-1" 
                                                    id={`day-${d}`}
                                                    placeholder="0"
                                                    value={days[d] || ''} 
                                                    onChange={e => handleDayChange(d, e.target.value)}
                                                    disabled={status !== 'Draft' && status !== 'Rejected'}
                                                    style={{height: '50px'}}
                                                />
                                                <label htmlFor={`day-${d}`} className="text-muted small" style={{transform: 'scale(0.85) translateY(-.5rem) translateX(0.15rem)'}}>{d}</label>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="card shadow-sm border-theme bg-card">
                            <div className="card-body">
                                <h5 className="card-title text-main">Submission</h5>
                                <p className="text-muted-theme small">Please upload the signed PDF confirmation from your client manager.</p>
                                <div className="mb-3">
                                    <input type="file" className="form-control" onChange={e => setFile(e.target.files[0])} disabled={status !== 'Draft' && status !== 'Rejected'} />
                                </div>
                                <button 
                                    className="btn btn-success w-100 py-3 fw-bold" 
                                    onClick={handleSubmit} 
                                    disabled={status === 'Submitted' || status === 'Approved' || uploading}
                                >
                                    {uploading ? 'Uploading & Submitting...' : (status === 'Approved' ? 'Timesheet Approved' : 'Submit for Approval')}
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* --- RIGHT COLUMN: SUBMISSION HISTORY --- */}
            <div className="col-lg-4">
                <h4 className="fw-bold text-main mb-4">Submission History</h4>
                <div className="card shadow-sm border-theme bg-card" style={{maxHeight: '600px', overflowY: 'auto'}}>
                    <div className="card-body p-0">
                        {submissionHistory.length > 0 ? (
                            <ul className="list-group list-group-flush">
                                {submissionHistory.map((sub, index) => (
                                    <button 
                                        key={index} 
                                        className="list-group-item list-group-item-action bg-transparent border-theme d-flex justify-content-between align-items-center py-3"
                                        onClick={() => {
                                            setMonth(sub.month);
                                            setYear(sub.year);
                                        }}
                                    >
                                        <div>
                                            <h6 className="mb-0 text-main fw-bold">{sub.month} {sub.year}</h6>
                                            <small className="text-muted-theme">
                                                Submitted: {sub.updatedAt ? new Date(sub.updatedAt).toLocaleDateString() : 'N/A'}
                                            </small>
                                        </div>
                                        <div className="text-end">
                                            <span className={`badge ${getStatusBadge(sub.status)} mb-1`}>{sub.status}</span>
                                            <br/>
                                            <small className="text-main fw-bold">{sub.totalHours} hrs</small>
                                        </div>
                                    </button>
                                ))}
                            </ul>
                        ) : (
                            <div className="p-4 text-center text-muted-theme">
                                <i className="bi bi-clock-history fs-1 d-block mb-2"></i>
                                <p>No history found.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

        </div>
      </div>
    </div>
  );
}